import { useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { LifeBuoy, Loader2, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const CATEGORIES = [
  { v: "technical", label: "Technical issue" },
  { v: "billing", label: "Billing & plans" },
  { v: "account", label: "Account & access" },
  { v: "general", label: "General question" },
];

export default function SupportPage() {
  const [category, setCategory] = useState("technical");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      await apiClient.post("/support", { category, subject, message });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="h-20 flex items-center px-6 sm:px-8">
        <div>
          <h1 className="font-display text-3xl font-700 tracking-tight">Support</h1>
          <p className="text-sm text-ink/60">Questions about billing, technical issues, or anything else — we're here.</p>
        </div>
      </div>

      <div className="p-6 lg:p-8 pt-2 max-w-2xl">
        {sent ? (
          <div className="nb-card bg-grass/15 border-2 border-ink p-8 text-center" data-testid="support-success">
            <div className="h-14 w-14 mx-auto mb-4 rounded-2xl bg-grass border-2 border-ink flex items-center justify-center shadow-nb-sm">
              <CheckCircle2 className="h-7 w-7 text-white" />
            </div>
            <h2 className="font-display text-2xl font-700 mb-1">Message received! 🚢</h2>
            <p className="text-ink/60 font-600 mb-5">Our team will get back to you by email shortly.</p>
            <button onClick={() => { setSent(false); setSubject(""); setMessage(""); setCategory("technical"); }}
              data-testid="support-new-ticket"
              className="text-sm font-800 px-5 py-2.5 rounded-xl border-2 border-ink bg-white shadow-nb-sm hover:bg-cream">
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="nb-card bg-white border-2 border-ink p-6 shadow-nb-sm space-y-4" data-testid="support-form">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="h-10 w-10 rounded-2xl bg-orange border-2 border-ink flex items-center justify-center shadow-nb-sm">
                <LifeBuoy className="h-5 w-5 text-white" />
              </div>
              <p className="font-700">Contact our team</p>
            </div>

            <div>
              <label className="label-overline mb-1.5 block">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="border-2 border-ink rounded-xl font-700 bg-white" data-testid="support-category"><SelectValue /></SelectTrigger>
                <SelectContent className="border-2 border-ink rounded-2xl">
                  {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="label-overline mb-1.5 block">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="support-subject"
                placeholder="Brief summary of your request"
                className="w-full border-2 border-ink rounded-xl px-3.5 py-2.5 font-600 bg-white outline-none focus:shadow-nb-sm" />
            </div>

            <div>
              <label className="label-overline mb-1.5 block">Message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} data-testid="support-message"
                placeholder="Tell us what's going on. Include any details that help us help you faster."
                className="w-full border-2 border-ink rounded-xl px-3.5 py-2.5 font-600 bg-white outline-none focus:shadow-nb-sm resize-y" />
            </div>

            <button type="submit" disabled={sending || !subject.trim() || !message.trim()} data-testid="support-submit"
              className="w-full flex items-center justify-center gap-2 font-800 px-5 py-3 rounded-xl bg-orange text-white border-2 border-ink shadow-nb-sm hover:translate-y-[-1px] transition-transform disabled:opacity-50 disabled:translate-y-0">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />} Send message
            </button>
            <p className="text-xs text-ink/50 font-600 text-center">Or email us directly at <a href="mailto:hello@tugboatapp.com" className="font-800 underline">hello@tugboatapp.com</a></p>
          </form>
        )}
      </div>
    </div>
  );
}
